// Organization S3 service to interface with S3-based organization data
import { Organization, OrganizationData, L7ManagerData } from './organizationService';
import { apiKeyService } from './apiKeyService';

// Re-export the L7ManagerData interface for use in other components
export type { L7ManagerData };

// API Configuration
const API_BASE_URL = process.env.REACT_APP_API_URL;
const ORGANIZATIONS_ENDPOINT = `${API_BASE_URL}/api/organizations`;

// No caching — always fetch fresh organization data from S3


// Enhanced interfaces for S3 storage
export interface OrganizationMetadata {
  fileId: string;
  organizationId: string;
  organizationName: string;
  version: string;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  lastModifiedBy: string;
}

export interface S3OrganizationData {
  metadata: OrganizationMetadata;
  organization: Organization;
}

export interface OrganizationListItem {
  id: string;
  name: string;
  fileName: string;
  lastUpdated: string;
  version: string;
}

// API Response interfaces
interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
  count?: number;
}

// Helper function to get auth headers with API key and Midway token
const getAuthHeaders = async (): Promise<Record<string, string>> => {
  console.log('🔐 [ORG-S3] Fetching auth headers...');
  
  try {
    // Use centralized API key service which includes both x-api-key and Authorization headers
    const headers = await apiKeyService.getApiKeyHeaders();
    
    console.log('✅ [ORG-S3] Auth headers retrieved:', {
      hasApiKey: !!headers['x-api-key'],
      hasAuthorization: !!headers['Authorization'],
      apiKeyLength: headers['x-api-key']?.length || 0,
      authHeaderLength: headers['Authorization']?.length || 0
    });
    
    return headers;
  } catch (error) {
    console.error('❌ [ORG-S3] Failed to get API key headers:', error);
    // Return basic headers without API key (will likely fail but allows graceful degradation)
    return {
      'Content-Type': 'application/json',
    };
  }
};

// Helper function to make API calls
const makeApiCall = async <T>(
  url: string,
  options: RequestInit = {}
): Promise<ApiResponse<T>> => {
  try {
    const headers = await getAuthHeaders();
    
    console.log('📡 [ORG-S3] Making API call:', {
      url,
      method: options.method || 'GET',
      hasApiKey: !!headers['x-api-key'],
      hasAuth: !!headers['Authorization']
    });
    
    const response = await fetch(url, {
      headers: {
        ...headers,
        ...options.headers,
      },
      ...options,
    });

    console.log('📥 [ORG-S3] API response:', {
      status: response.status,
      ok: response.ok,
      statusText: response.statusText
    });

    const data = await response.json();

    if (!response.ok) {
      console.error('❌ [ORG-S3] API error response:', data);
      throw new Error(data.error || `HTTP error! status: ${response.status}`);
    }

    return data;
  } catch (error) {
    console.error('❌ [ORG-S3] API call failed:', error);
    throw error;
  }
};

// Create new organization in S3
export const createOrganizationS3 = async (organizationData: Organization): Promise<S3OrganizationData> => {
  try {
    const response = await makeApiCall<S3OrganizationData>(
      ORGANIZATIONS_ENDPOINT,
      {
        method: 'POST',
        body: JSON.stringify(organizationData),
      }
    );

    if (!response.success || !response.data) {
      throw new Error(response.error || 'Failed to create organization');
    }

    console.log('✅ Organization created');

    return response.data;
  } catch (error) {
    console.error('Error creating organization:', error);
    throw error;
  }
};

// Get organization by ID from S3
export const getOrganizationS3 = async (organizationId: string): Promise<S3OrganizationData> => {
  try {
    const response = await makeApiCall<S3OrganizationData>(
      `${ORGANIZATIONS_ENDPOINT}/${organizationId}`
    );

    if (!response.success || !response.data) {
      throw new Error(response.error || 'Organization not found');
    }

    return response.data;
  } catch (error) {
    console.error('Error getting organization:', error);
    throw error;
  }
};

// Update organization in S3
export const updateOrganizationS3 = async (
  organizationId: string,
  updateData: Partial<Organization>
): Promise<S3OrganizationData> => {
  try {
    const response = await makeApiCall<S3OrganizationData>(
      `${ORGANIZATIONS_ENDPOINT}/${organizationId}`,
      {
        method: 'PUT',
        body: JSON.stringify(updateData),
      }
    );

    if (!response.success || !response.data) {
      throw new Error(response.error || 'Failed to update organization');
    }

    console.log('✅ Organization updated');

    return response.data;
  } catch (error) {
    console.error('Error updating organization:', error);
    throw error;
  }
};

// Delete organization from S3
export const deleteOrganizationS3 = async (organizationId: string): Promise<void> => {
  try {
    const response = await makeApiCall<{ message: string }>(
      `${ORGANIZATIONS_ENDPOINT}/${organizationId}`,
      {
        method: 'DELETE',
      }
    );

    if (!response.success) {
      throw new Error(response.error || 'Failed to delete organization');
    }

    console.log('✅ Organization deleted');
  } catch (error) {
    console.error('Error deleting organization:', error);
    throw error;
  }
};

// List all organizations from S3
export const listOrganizationsS3 = async (): Promise<OrganizationListItem[]> => {
  try {
    const response = await makeApiCall<OrganizationListItem[]>(
      ORGANIZATIONS_ENDPOINT
    );

    if (!response.success) {
      throw new Error(response.error || 'Failed to list organizations');
    }

    return response.data || [];
  } catch (error) {
    console.error('Error listing organizations:', error);
    throw error;
  }
};

// Get all organizations data (for compatibility with existing code)
export const getAllOrganizationsS3 = async (): Promise<Organization[]> => {
  try {
    const organizationList = await listOrganizationsS3();
    
    if (!organizationList || organizationList.length === 0) {
      return [];
    }

    const organizations: Organization[] = [];

    for (const orgItem of organizationList) {
      try {
        const fullOrgData = await getOrganizationS3(orgItem.id);
        organizations.push(fullOrgData.organization);
      } catch (error) {
        console.error(`Error fetching organization ${orgItem.name}:`, error);
      }
    }

    return organizations;
  } catch (error) {
    console.error('❌ Failed to load organizations:', error);
    return [];
  }
};


// Always fetch fresh organization data from S3 (no caching)
export const getOrganizationData = async (_forceRefresh: boolean = false, _pageId?: string): Promise<OrganizationData | null> => {
  try {
    const organizations = await getAllOrganizationsS3();
    
    const orgData = {
      organizations,
      selectedOrgId: organizations[0]?.id || ''
    };

    return orgData;
  } catch (error) {
    console.error('❌ Failed to fetch organization data:', error);
    
    return {
      organizations: [],
      selectedOrgId: ''
    };
  }
};

// Get all L7 managers from S3 organizations (reads from portfolio directors if available)
export const getAllL7Managers = async (organizationId?: string): Promise<L7ManagerData[]> => {
  try {
    const orgData = await getOrganizationData();
    if (!orgData) return [];

    const l7Managers: L7ManagerData[] = [];
    const orgsToProcess = organizationId 
      ? orgData.organizations.filter(org => org.id === organizationId || org.asanaProject === organizationId)
      : orgData.organizations;

    orgsToProcess.forEach(org => {
      // New model: collect directors from all portfolios
      const portfolios = (org as any).portfolios as any[] | undefined;
      const allDirectors: any[] = [];
      
      if (portfolios && portfolios.length > 0) {
        // If org has portfolios, get directors from each portfolio
        // Also check if organizationId matches a portfolio's asanaProjectId
        portfolios.forEach((p: any) => {
          if (!organizationId || org.id === organizationId || p.asanaProjectId === organizationId) {
            (p.directors || []).forEach((d: any) => allDirectors.push(d));
          }
        });
      }
      
      // Fallback: also include org-level directors (legacy model)
      if (allDirectors.length === 0 && org.directors) {
        org.directors.forEach((d: any) => allDirectors.push(d));
      }

      allDirectors.forEach((director: any) => {
        director.directReports.forEach((report: any) => {
          const actualHC = report.headcount.sde + report.headcount.sdm + 
                          report.headcount.tpm + report.headcount.pmt + 
                          report.headcount.bie + report.headcount.srManager + 
                          report.headcount.other;

          const tier1Service = (report as any).tier1Service || 0;
          const tier2Service = (report as any).tier2Service || 0;
          
          if (tier1Service > 0 || tier2Service > 0) {
          l7Managers.push({
            name: report.name,
            targetSDE: report.headcount.sde,
            actualSDE: report.headcount.sde, // This is correct - only counts SDE from headcount breakdown, not Principal SDE roles
            totalTargetHC: report.targetHC,
            totalActualHC: actualHC,
            rtb: report.rtb,
            role: report.role,
            email: report.email,
            tier1Service: tier1Service,
            tier2Service: tier2Service
          });
          }
        });
      });
    });

    return l7Managers;
  } catch (error) {
    console.error('Error getting L7 managers:', error);
    return [];
  }
};

// No caching — always fetch fresh L7 manager data
export const findL7ManagerByName = async (name: string, organizationId?: string): Promise<L7ManagerData | null> => {
  try {
    const l7Managers = await getAllL7Managers(organizationId);
    
    let match = l7Managers.find(manager => 
      manager.name.toLowerCase() === name.toLowerCase()
    );
    
    if (!match) {
      match = l7Managers.find(manager => {
        const managerNames = manager.name.toLowerCase().split(' ');
        const searchNames = name.toLowerCase().split(' ');
        
        return searchNames.some(searchName => 
          managerNames.some(managerName => 
            managerName.includes(searchName) || searchName.includes(managerName)
          )
        );
      });
    }
    
    return match || null;
  } catch (error) {
    console.error('Error finding L7 manager:', error);
    return null;
  }
};

export const getAggregatedL7Data = async (l7ManagerNames: string[], organizationId?: string) => {
  try {
    const matchedManagers: L7ManagerData[] = [];
    
    for (const name of l7ManagerNames) {
      const manager = await findL7ManagerByName(name, organizationId);
      if (manager) {
        matchedManagers.push(manager);
      }
    }

    const totalTargetSDE = matchedManagers.reduce((sum, manager) => sum + manager.targetSDE, 0);
    const totalActualSDE = matchedManagers.reduce((sum, manager) => sum + manager.actualSDE, 0);
    const totalTargetHC = matchedManagers.reduce((sum, manager) => sum + manager.totalTargetHC, 0);
    const totalActualHC = matchedManagers.reduce((sum, manager) => sum + manager.totalActualHC, 0);
    const totalRTB = matchedManagers.reduce((sum, manager) => sum + manager.rtb, 0);
    
    return {
      matchedCount: matchedManagers.length,
      totalCount: l7ManagerNames.length,
      totalTargetSDE,
      totalActualSDE,
      totalTargetHC,
      totalActualHC,
      totalRTB,
      coveragePercentage: l7ManagerNames.length > 0 ? (matchedManagers.length / l7ManagerNames.length) * 100 : 0,
      matchedManagers
    };
  } catch (error) {
    console.error('Error getting aggregated L7 data:', error);
    return {
      matchedCount: 0,
      totalCount: l7ManagerNames.length,
      totalTargetSDE: 0,
      totalActualSDE: 0,
      totalTargetHC: 0,
      totalActualHC: 0,
      totalRTB: 0,
      coveragePercentage: 0,
      matchedManagers: []
    };
  }
};

// No-op for backward compatibility (no caches to clear)
export const clearOrganizationCache = (): void => {
  // No caching to clear — data is always fetched fresh
};

export const refreshOrganizationData = async (): Promise<OrganizationData | null> => {
  return await getOrganizationData(true);
};

// export const generateOrganizationId = (): string => {
//   return `org-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
// };

const sanitizeOrganizationName = (name: string): string => {
  return name
    .toLowerCase()                    // Convert to lowercase
    .replace(/[^a-z0-9\s-]/g, '')     // Remove special characters except spaces and hyphens
    .replace(/\s+/g, '')              // Remove all spaces
    .replace(/-+/g, '-')              // Replace multiple hyphens with single hyphen
    .replace(/^-|-$/g, '')            // Remove leading/trailing hyphens
    .substr(0, 20);                   // Limit length to keep IDs manageable
};

export const generateOrganizationId = (organizationName?: string): string => {
  const timestamp = Date.now();
  const randomString = Math.random().toString(36).substr(2, 9);
  
  if (organizationName) {
    const sanitizedName = sanitizeOrganizationName(organizationName);
    return `org-${sanitizedName}-${timestamp}-${randomString}`;
  }
  
  // Fallback to old format if no name provided
  return `org-${timestamp}-${randomString}`;
};

export const validateOrganizationData = (organization: Partial<Organization>): string[] => {
  const errors: string[] = [];

  if (!organization.id) {
    errors.push('Organization ID is required');
  }

  if (!organization.name || organization.name.trim().length === 0) {
    errors.push('Organization name is required');
  }

  if (organization.directors) {
    organization.directors.forEach((director, index) => {
      if (!director.id) {
        errors.push(`Director ${index + 1}: ID is required`);
      }
      if (!director.name || director.name.trim().length === 0) {
        errors.push(`Director ${index + 1}: Name is required`);
      }
      if (!director.email || !director.email.includes('@')) {
        errors.push(`Director ${index + 1}: Valid email is required`);
      }
    });
  }

  return errors;
};
